/**
 * Normalize order-type strings for comparison (dine-in / dineIn / Dine-In → dinein).
 */
export function normalizeOrderTypeKey(orderType) {
  return String(orderType || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");
}

/** Dine-in and browse share table context + in-venue pricing. */
export function isDineInLikeOrderType(orderType) {
  const key = normalizeOrderTypeKey(orderType);
  return key === "dinein" || key === "browse";
}

export function isBrowseOrderType(orderType) {
  return normalizeOrderTypeKey(orderType) === "browse";
}

export function isDineInOrderType(orderType) {
  return normalizeOrderTypeKey(orderType) === "dinein";
}

/**
 * Statuses that stop a table taking new online orders.
 * "seated" is orderable on purpose — guests are at the table, which is exactly
 * when the QR should work.
 * Aliases cover API / POS wording variants (e.g. "Block", "Reserve").
 */
const NON_ORDERABLE_TABLE_STATUSES = new Set([
  "reserved",
  "reserve",
  "blocked",
  "block",
]);

function normalizeTableStatus(status) {
  return String(status || "")
    .toLowerCase()
    .trim();
}

export function isTableNonOrderableStatus(status) {
  const key = normalizeTableStatus(status);
  if (!key) return false;
  if (NON_ORDERABLE_TABLE_STATUSES.has(key)) return true;
  // Soft match: "Table Blocked", "is reserved", etc.
  if (key.includes("blocked") || key.includes("block")) return true;
  if (key.includes("reserved") || key.includes("reserve")) return true;
  return false;
}

/** True when guest arrived via table QR / table landing (has a bound table). */
export function hasTableOrderContext(customerInfo) {
  if (!customerInfo || typeof customerInfo !== "object") return false;
  const tableId = customerInfo.tableId;
  if (tableId != null && String(tableId).trim() !== "" && String(tableId) !== "0") {
    return true;
  }
  const tableNumber = String(customerInfo.tableNumber || "").trim();
  const tableName = String(customerInfo.tableName || "").trim();
  return Boolean(tableNumber || tableName);
}

/** Bound to a table that is also allowed to take new orders. */
export function canOrderFromTableContext(customerInfo) {
  if (!hasTableOrderContext(customerInfo)) return false;
  return !isTableNonOrderableStatus(customerInfo?.tableStatus);
}

/**
 * Polite copy when a table cannot accept online orders (reserved / blocked).
 * @param {"landing"|"toast"|"browseHint"|"checkout"|"picker"} tone
 */
export function getTableUnavailableMessage(status, options = {}) {
  const {
    tableLabel = "",
    tone = "toast",
  } = options;
  const key = normalizeTableStatus(status);
  const name = String(tableLabel || "").trim();
  const tableRef = name ? `"${name}"` : "this table";

  const isReserved = key.includes("reserve");
  const isBlocked = key.includes("block");

  if (tone === "landing") {
    if (isReserved) {
      return `Thank you for visiting. ${name || "This table"} is currently reserved for another guest, so online ordering isn’t available here. Please speak with a member of our team — we’ll be happy to help seat you or take your order.`;
    }
    if (isBlocked) {
      return `Thank you for your patience. ${name || "This table"} isn’t available for online ordering at the moment. Please speak with a member of our team and we’ll gladly assist you.`;
    }
    return `Thank you for visiting. Online ordering isn’t available at ${tableRef} right now. Please speak with a member of our team and we’ll be happy to help.`;
  }

  if (tone === "landingLead") {
    if (isReserved) {
      return "This table is reserved just now. You’re welcome to browse the menu or leave a review, and our team can help with seating or ordering.";
    }
    if (isBlocked) {
      return "This table isn’t available for online orders at the moment. You’re welcome to browse the menu or leave a review, and our team will gladly help.";
    }
    return "Online ordering isn’t available at this table right now. You’re welcome to browse the menu or leave a review, and our team can assist you.";
  }

  if (tone === "browseHint") {
    if (isReserved) {
      return "This table is reserved for another guest, so ordering isn’t available here. Please ask a team member for assistance.";
    }
    if (isBlocked) {
      return "This table isn’t available for ordering right now. Please ask a team member for assistance.";
    }
    return "Ordering isn’t available at this table right now. Please ask a team member for assistance.";
  }

  if (tone === "checkout" || tone === "picker") {
    if (isReserved) {
      return `${name || "This table"} is currently reserved. Please select a different table, or ask a team member for help.`;
    }
    if (isBlocked) {
      return `${name || "This table"} isn’t available right now. Please select a different table, or ask a team member for help.`;
    }
    return `${name || "This table"} isn’t available for ordering. Please select a different table, or ask a team member for help.`;
  }

  // toast (default)
  if (isReserved) {
    return `Thank you for your understanding — ${name || "this table"} is currently reserved, so we can’t start an order here. Please ask a team member for assistance.`;
  }
  if (isBlocked) {
    return `Thank you for your patience — ${name || "this table"} isn’t available for ordering at the moment. Please ask a team member for assistance.`;
  }
  return `Thank you for your understanding — ordering isn’t available at ${tableRef} right now. Please ask a team member for assistance.`;
}

/** Browse-mode CTA / toasts for table QR only — not for home “Browse menu”. */
export function shouldShowOrderFromTableCta(orderType, customerInfo) {
  return isBrowseOrderType(orderType) && canOrderFromTableContext(customerInfo);
}

export function browseModeOrderHint(customerInfo) {
  if (canOrderFromTableContext(customerInfo)) {
    return "Switch to ordering to add items — tap “Order from this table” below.";
  }
  if (hasTableOrderContext(customerInfo)) {
    return getTableUnavailableMessage(customerInfo?.tableStatus, { tone: "browseHint" });
  }
  return "This is browse-only. Choose Delivery or Collection on the home page to place an order.";
}

/**
 * Home “Browse menu” (no table QR) — open the start-order modal instead of a toast.
 * Table QR browse keeps the existing toast / “Order from this table” CTA.
 */
export function shouldOpenBrowseStartOrderModal(orderType, customerInfo) {
  return isBrowseOrderType(orderType) && !hasTableOrderContext(customerInfo);
}
